import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  ChevronDown,
  ChevronUp,
  BookOpen,
  Swords,
  Zap,
  Scroll,
  Plus,
  Settings
} from 'lucide-react';

interface AdvancedStrategyOptionsProps {
  themes: string;
  setThemes: (themes: string) => void;
  cardTypes: string[];
  setCardTypes: (types: string[]) => void;
  creatureTypes: string[];
  setCreatureTypes: (types: string[]) => void;
}

const CARD_TYPE_OPTIONS = [
  { type: "Instant", icon: "⚡" },
  { type: "Sorcery", icon: "📜" },
  { type: "Creature", icon: "🐉" },
  { type: "Enchantment", icon: "✨" },
  { type: "Artifact", icon: "⚙️" },
  { type: "Planeswalker", icon: "👑" },
  { type: "Land", icon: "🏔️" }
];

const POPULAR_MECHANICS = [
  "+1/+1 Counter", "Equipment", "Aura", "Mill", "Saga", "Copy", "Cascade", "Energy"
];

const DEFAULT_CREATURE_TYPES = [
  "Angel", "Beast", "Dinosaur", "Dragon", "Elf", "Goblin", "Human", 
  "Merfolk", "Ninja", "Sliver", "Spirit", "Squirrel", "Unicorn", 
  "Vampire", "Wizard", "Zombie"
];

export const AdvancedStrategyOptions = ({ 
  themes, 
  setThemes, 
  cardTypes, 
  setCardTypes, 
  creatureTypes, 
  setCreatureTypes 
}: AdvancedStrategyOptionsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customCreatureType, setCustomCreatureType] = useState('');
  const [displayedCreatureTypes, setDisplayedCreatureTypes] = useState(DEFAULT_CREATURE_TYPES);

  const handleAddCustomCreatureType = () => {
    const newType = customCreatureType.trim();
    if (newType) {
      if (!displayedCreatureTypes.includes(newType)) {
        setDisplayedCreatureTypes([...displayedCreatureTypes, newType].sort());
      }
      if (!creatureTypes.includes(newType)) {
        setCreatureTypes([...creatureTypes, newType]);
      }
    }
    setCustomCreatureType('');
  };

  useEffect(() => {
    const allTypes = new Set([...displayedCreatureTypes, ...creatureTypes]);
    if (allTypes.size !== displayedCreatureTypes.length) {
      setDisplayedCreatureTypes(Array.from(allTypes).sort());
    }
  }, [creatureTypes, displayedCreatureTypes]);

  const themeParts = themes.split(',').map(t => t.trim()).filter(Boolean);
  const selectedMechanics = themeParts.filter(t => POPULAR_MECHANICS.includes(t));

  const handleMechanicsChange = (newlySelectedMechanics: string[]) => {
    const otherThemes = themeParts.filter(t => !POPULAR_MECHANICS.includes(t));
    const allThemes = [...otherThemes, ...newlySelectedMechanics];
    setThemes(allThemes.join(', '));
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button variant="outline" className="w-full justify-between text-sm md:text-base h-9 md:h-10 touch-manipulation">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            <span>Advanced Options</span>
          </div>
          {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="space-y-3 md:space-y-4 mt-3 md:mt-4">
        {/* Card Types */}
        <Card className="border-dashed border-primary/30 bg-gradient-to-br from-background to-muted/20">
          <CardContent className="p-3 md:p-4 space-y-2 md:space-y-3">
            <div className="flex items-center gap-2 mb-1 md:mb-2">
              <BookOpen className="w-4 h-4 text-primary" />
              <Label className="font-medium text-sm md:text-base">Card Types</Label>
            </div>
            
            <ToggleGroup 
              type="multiple" 
              variant="outline" 
              value={cardTypes} 
              onValueChange={setCardTypes} 
              className="flex flex-wrap justify-center gap-1 md:gap-2"
            >
              {CARD_TYPE_OPTIONS.map(({ type, icon }) => (
                <ToggleGroupItem 
                  key={type} 
                  value={type}
                  className="transition-all duration-200 hover:scale-105 data-[state=on]:bg-primary/20 data-[state=on]:border-primary text-xs md:text-sm h-8 md:h-10 px-2 md:px-3 touch-manipulation"
                >
                  <span className="mr-1">{icon}</span>
                  {type}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            
            {cardTypes.length > 0 && (
              <div className="text-center text-xs text-muted-foreground">
                {cardTypes.length} card type{cardTypes.length !== 1 ? 's' : ''} selected
              </div>
            )}
          </CardContent>
        </Card>

        {/* Creature Types */}
        <Card className="border-dashed border-blue-500/30 bg-gradient-to-br from-blue-500/5 to-blue-500/10">
          <CardContent className="p-3 md:p-4 space-y-2 md:space-y-3">
            <div className="flex items-center gap-2 mb-1 md:mb-2">
              <Swords className="w-4 h-4 text-blue-500" />
              <Label className="font-medium text-sm md:text-base">Creature Types</Label>
            </div>
            
            <ToggleGroup 
              type="multiple" 
              variant="outline" 
              value={creatureTypes} 
              onValueChange={setCreatureTypes} 
              className="flex flex-wrap justify-center gap-1 md:gap-2"
            >
              {displayedCreatureTypes.map(type => (
                <ToggleGroupItem 
                  key={type} 
                  value={type}
                  className="transition-all duration-200 hover:scale-105 data-[state=on]:bg-blue-500/20 data-[state=on]:border-blue-500 text-xs md:text-sm h-8 md:h-10 px-2 md:px-3 touch-manipulation"
                >
                  {type}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            
            <div className="flex items-center gap-2">
              <Input
                placeholder="Add custom creature type"
                value={customCreatureType}
                onChange={(e) => setCustomCreatureType(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCustomCreatureType();
                  }
                }}
                className="flex-1 text-sm md:text-base h-8 md:h-10"
              />
              <Button 
                onClick={handleAddCustomCreatureType}
                size="sm"
                className="shrink-0 h-8 md:h-10 px-2 md:px-3 text-xs md:text-sm"
                disabled={!customCreatureType.trim()}
              >
                <Plus className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                <span className="hidden sm:inline">Add</span>
                <span className="sm:hidden">+</span>
              </Button>
            </div>
            
            {creatureTypes.length > 0 && (
              <div className="text-center text-xs text-muted-foreground">
                {creatureTypes.length} creature type{creatureTypes.length !== 1 ? 's' : ''} selected
              </div>
            )}
          </CardContent>
        </Card>

        {/* Popular Mechanics */}
        <Card className="border-dashed border-purple-500/30 bg-gradient-to-br from-purple-500/5 to-purple-500/10">
          <CardContent className="p-3 md:p-4 space-y-2 md:space-y-3">
            <div className="flex items-center gap-2 mb-1 md:mb-2">
              <Zap className="w-4 h-4 text-purple-500" />
              <Label className="font-medium text-sm md:text-base">Popular Mechanics</Label>
            </div>
            
            <ToggleGroup 
              type="multiple" 
              variant="outline" 
              value={selectedMechanics} 
              onValueChange={handleMechanicsChange} 
              className="flex flex-wrap justify-center gap-1 md:gap-2"
            >
              {POPULAR_MECHANICS.map(mechanic => (
                <ToggleGroupItem 
                  key={mechanic} 
                  value={mechanic}
                  className="transition-all duration-200 hover:scale-105 data-[state=on]:bg-purple-500/20 data-[state=on]:border-purple-500 text-xs md:text-sm h-8 md:h-10 px-2 md:px-3 touch-manipulation"
                >
                  {mechanic}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            
            {selectedMechanics.length > 0 && (
              <div className="text-center text-xs text-muted-foreground">
                {selectedMechanics.length} mechanic{selectedMechanics.length !== 1 ? 's' : ''} selected
              </div>
            )}
          </CardContent>
        </Card>

        {/* Custom Themes */}
        <Card className="border-dashed border-green-500/30 bg-gradient-to-br from-green-500/5 to-green-500/10">
          <CardContent className="p-3 md:p-4 space-y-2 md:space-y-3">
            <div className="flex items-center gap-2 mb-1 md:mb-2">
              <Scroll className="w-4 h-4 text-green-500" />
              <Label htmlFor="themes-input" className="font-medium text-sm md:text-base">Custom Themes</Label>
            </div>
            
            <Input
              id="themes-input"
              placeholder="e.g., mill, +1/+1 counters, treasures"
              value={themes}
              onChange={(e) => setThemes(e.target.value)}
              className="border-green-500/20 text-sm md:text-base h-8 md:h-10"
            />
            
            <p className="text-xs text-muted-foreground">
              Separate themes with commas. These can be mechanics, strategies, or any other deck themes.
            </p>
            
            {themes && (
              <div className="flex flex-wrap gap-1 mt-2">
                {themeParts.filter(t => !POPULAR_MECHANICS.includes(t)).map((theme, index) => (
                  <span 
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-700 border border-green-500/30"
                  >
                    {theme}
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </CollapsibleContent>
    </Collapsible>
  );
};